import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { color } from "@vds-tokens/core";
import { useDebounce } from "hooks";
import {
  ButtonGroup,
  Input,
  Loader,
  Modal,
  Notification,
  Text,
} from "@vds/core";
import { Icon } from "@vds/icons";
import {
  setIsPOYDFlow,
  setPOYDBillAmount,
  setPOYDBillUploadSuccess,
  uploadPOYDDocument,
  removeAllPOYDDocuments,
  validatePOYDMTN,
  setPOYDMtnValidation,
} from "pages/Mobility/ducks/MobilitySlice";
import { formatPriceString } from "utils/commonUtils";
import FileUpload from "common/components/FileUpload/FileUpload";
import { set } from "lodash";
import { TrailingTooltip } from "@vds/tooltips";

// Regex for validating US currency values (commas optional, dollar sign optional, leading zeroes optional, two decimals optional)
const usCurrencyRegex =
  /^\$?\s*([0-9]{1,3}(?:,[0-9]{3})*|[0-9]+)?(\.[0-9]{2})?$/;

// Regex for validating US telephone numbers with optional hyphens as separators (e.g., 123-456-7890 or 1234567890)
const usPhoneRegex = /^\d{3}-?\d{3}-?\d{4}$/;

const POYDModal = ({
  opened,
  onOpenedChange = () => {},
  onConfirm = () => {},
}) => {
  const { t } = useTranslation(["app"], { keyPrefix: "poyd" });
  const dispatch = useDispatch();
  const mobilityPageLoading = useSelector(
    (state) => state.MobilityPage.loading
  );
  const isUploadSuccess = useSelector(
    (state) => state.MobilityPage.poydBillUploadSuccess
  );
  const poydMtnValidationLoading = useSelector(
    (state) => state.MobilityPage.poydMtnValidationLoading
  );
  const isMTNEligible = useSelector(
    (state) => state.MobilityPage.poydMtnValidation
  );
  const phoneNumberRef = useRef(null);
  const billAmountRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(" ");
  const [phoneNumberValid, setPhoneNumberValid] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [billAmount, setBillAmount] = useState("$");
  const [billAmountValid, setBillAmountValid] = useState(false);
  const [billAmountError, setBillAmountError] = useState(false);
  const [billFiles, setBillFiles] = useState({});
  const [billFilesError, setBillFilesError] = useState(false);

  const maxAmount = t("maxAmount", { returnObjects: true });
  const maxAmountFormatted = formatPriceString(maxAmount, true);

  // Debounce phone number for validation
  useDebounce(
    () => {
      if (phoneNumber.trim() && phoneNumberValid) {
        dispatch(setPOYDMtnValidation(null));
        dispatch(
          validatePOYDMTN({ mtn: phoneNumber.trim().replaceAll("-", "") })
        );
      }
    },
    500,
    [phoneNumber, phoneNumberValid]
  );

  useEffect(() => {
    // handle document upload success
    if (isUploadSuccess) {
      dispatch(setPOYDBillAmount(billAmount));
      setShowComplete(true);
      dispatch(setPOYDBillUploadSuccess(null))
    } else if (isUploadSuccess === false) {
      // handle document upload failure
      console.error("Error uploading POYD document");
      setBillFilesError({
        kind: "error",
        title: t("modal.uploadBill.errorMessages.uploadFailed.title"),
      });
    }
  }, [isUploadSuccess]);

  useEffect(() => {
    if (isMTNEligible === false) {
      setPhoneNumberError(t("modal.transferNumber.errorInvalid"));
    } else if (isMTNEligible === true) {
      setPhoneNumberError(false);
    }
  }, [isMTNEligible]);

  useEffect(() => {
    dispatch(setPOYDMtnValidation(null));
  }, []);

  const resetState = ({ timeout = 0 } = {}) => {
    // use timeout when closing modal to allow animations to complete
    // before resetting state, otherwise it will flash the initial state
    setTimeout(() => {
      setShowComplete(false);
      setPhoneNumber(" ");
      setPhoneNumberValid(false);
      setPhoneNumberError(false);
      setBillAmount("$");
      setBillAmountValid(false);
      setBillAmountError(false);
      dispatch(setPOYDBillUploadSuccess(null));
      dispatch(setPOYDMtnValidation(null));
    }, timeout);
  };

  const handlePhoneNumberInput = (val) => {
    // if (
    //   !phoneNumberRef.current ||
    //   document.activeElement !== phoneNumberRef.current
    // ) {
    //   // If the input is not focused, do not validate
    //   return;
    // }
    // dispatch(setPOYDMtnValidation(null));
    const value = val?.trim();
    if (!value) {
      setPhoneNumberError(t("modal.transferNumber.errorMissing"));
    } else if (usPhoneRegex.test(value)) {
      // setPhoneNumberError(false);
      dispatch(setPOYDMtnValidation(null));
      setPhoneNumber(value);
      setPhoneNumberValid(true);
    } else {
      setPhoneNumberError(t("modal.transferNumber.errorMissing"));
      setPhoneNumberValid(false);
    }
  };

  const handleBillAmountInput = (val) => {
    const value = val.startsWith("$") ? val : `$${val.replace(/[$]/g, "").trim()}`;
    setBillAmount(value);
    if (!value || val === "$") {
      setBillAmountError(t("modal.billAmount.errorMissing"));
    } else if (usCurrencyRegex.test(value)) {
      const numericValue = parseFloat(value.replace(/[$,]/g, ""));
      if (isNaN(numericValue) || numericValue <= 0) {
        setBillAmountError(t("modal.billAmount.errorInvalid"));
        setBillAmountValid(false);
      } else if (numericValue > maxAmount) {
        setBillAmountError(
          t("modal.billAmount.errorTooHigh", { maxAmount: maxAmountFormatted })
        );
        setBillAmountValid(false);
      } else {
        setBillAmountError(false);
        setBillAmountValid(true);
        // setBillAmount(value);
      }
    } else {
      setBillAmountError(t("modal.billAmount.errorMissing"));
      console.error("Invalid bill amount format");
    }
  };

  const handleBillFileUpload = ({ id, file, blob, contentType }) => {
    setBillFilesError(false);
    setBillFiles((prevFiles) => {
      const newFiles = { ...prevFiles };
      newFiles[id] = {
        file: file,
        documentContent: blob,
        contentType: contentType,
      };
      return newFiles;
    });
  };

  const handleBillFileRemove = ({ id }) => {
    setBillFilesError(false);
    setBillFiles((prevFiles) => {
      const newFiles = { ...prevFiles };
      delete newFiles[id];
      return newFiles;
    });
  };

  const handleSubmit = () => {
    const payload = {
      lines: Object.values(billFiles || {}).map((file) => ({
        documentContent: file.documentContent,
        // documentName: "POYD_BILL_RECEIPT",
        metadata: [
          {
            name: "Content-Type",
            value: file.contentType,
          },
        ],
      })),
    };
    dispatch(uploadPOYDDocument(payload));
  };

  return (
    <Modal
      closeButton={{ show: false }}
      enableBackButton={true}
      disableOutsideClick={true}
      opened={opened}
      onOpenedChange={onOpenedChange}
      width={showComplete ? "560px" : "768px"}
      height={showComplete ? "480px" : "100%"}
      title={{
        children: (
          <div
            dangerouslySetInnerHTML={{
              __html: showComplete
                ? t("modal.complete.title")
                : t("modal.title", { maxAmount: maxAmountFormatted }),
            }}
          />
        ),
      }}
    >
      <Loader active={!!mobilityPageLoading} fullscreen={false} />
      {showComplete ? (
        <CompletedScreen
          payoffAmount={billAmount}
          onContinue={() => {
            dispatch(setIsPOYDFlow(true));
            onOpenedChange(false);
            dispatch(setPOYDBillAmount(billAmount));
            // Call the onConfirm function passed from the parent component
            // to handle any additional logic after confirmation
            // For example, navigating to a different page or updating the state
            onConfirm();
            // Reset state after confirmation
            // to allow the modal to be reused without showing previous state
            // Use a timeout to allow animations to complete before resetting state
            resetState({ timeout: 1000 });
          }}
          onReupload={() => {
            dispatch(setPOYDBillAmount(null));
            dispatch(removeAllPOYDDocuments());
            resetState();
          }}
          onCancel={() => {
            dispatch(setPOYDBillAmount(null));
            dispatch(removeAllPOYDDocuments());
            onOpenedChange(false);
            resetState({ timeout: 1000 });
          }}
        />
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          <DummyInput />
          <HowItWorks />
          <FormContainer>
            <FormElement>
              <Text type="body" size="large" bold>
                {t("modal.transferNumber.heading")}
              </Text>
              <div style={{ position: "relative" }}>
                {!!poydMtnValidationLoading && (
                  <Loader
                    fullscreen={false}
                    active={poydMtnValidationLoading}
                  />
                )}
                <Input
                  type="tel"
                  label={t("modal.transferNumber.label")}
                  value={phoneNumber}
                  success={isMTNEligible}
                  successText={t("modal.transferNumber.successMessage")}
                  errorText={phoneNumberError}
                  error={!!phoneNumberError}
                  onChange={(e) => handlePhoneNumberInput(e.target.value)}
                  onBlur={(e) => handlePhoneNumberInput(e.target.value)}
                  ref={phoneNumberRef}
                  autoFocus={false}
                />
              </div>
            </FormElement>
            <FormElement>
              <Text type="body" size="large" bold>
                {t("modal.billAmount.heading")}
              </Text>
              <Input
                type="text"
                label={t("modal.billAmount.label")}
                helperText={
                  !billAmountError &&
                  t("modal.billAmount.helperText", {
                    maxAmount: maxAmountFormatted,
                  })
                }
                value={`${billAmount}`}
                errorText={billAmountError}
                error={!!billAmountError}
                onChange={(e) => handleBillAmountInput(e.target.value)}
                onBlur={(e) => handleBillAmountInput(e.target.value)}
                ref={billAmountRef}
                autoFocus={false}
              />
            </FormElement>
          </FormContainer>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <FileUploadTitleLockup>
              <div style={{display:"flex", flexDirection: "row", alignItems: "center"}}>
              <Text type="body" size="large" bold>
                {t("modal.uploadBill.heading")}
              </Text>
                <TrailingTooltip
                  typographySize="small"
                  typographyType="body"
                  typographyColor="#000000"
                  tooltipSize="small"
                  tooltipContent={t("modal.uploadBill.tooltip")}
                  bold={true}
                  inverted={false}
                />
                </div>
              <Text type="body" size="small">
                {t("modal.uploadBill.description")}
              </Text>
            </FileUploadTitleLockup>
            {!!billFilesError && (
              <Notification
                kind={billFilesError?.kind || "error"}
                title={billFilesError?.title}
                description={billFilesError?.description}
              />
            )}
            <FileUploadSection>
              <FileUploadWrapper>
                <FileUpload
                  id="poyd-bill-upload-1"
                  onFileUpload={handleBillFileUpload}
                  onFileRemove={handleBillFileRemove}
                  {...t("modal.uploadBill", { returnObjects: true })}
                />
              </FileUploadWrapper>
              <FileUploadWrapper>
                <FileUpload
                  id="poyd-bill-upload-2"
                  onFileUpload={handleBillFileUpload}
                  onFileRemove={handleBillFileRemove}
                  {...t("modal.uploadBill", { returnObjects: true })}
                />
              </FileUploadWrapper>
              <FileUploadWrapper>
                <FileUpload
                  id="poyd-bill-upload-3"
                  onFileUpload={handleBillFileUpload}
                  onFileRemove={handleBillFileRemove}
                  {...t("modal.uploadBill", { returnObjects: true })}
                />
              </FileUploadWrapper>
            </FileUploadSection>
          </div>
          <TradeInRequirements />
          <ButtonGroup
            alignment="center"
            buttonWidth="100%"
            items={[
              {
                children: t("modal.continueButtonText"),
                kind: "primary",
                size: "large",
                onClick: () => {
                  if (
                    phoneNumber.trim().length &&
                    billAmount.trim().length &&
                    !billAmountError &&
                    !phoneNumberError &&
                    Object.keys(billFiles || {}).length > 0 
                    // isMTNEligible === true
                  ) {
                    handleSubmit();
                  } else {
                    if (Object.keys(billFiles || {}).length === 0) {
                      setBillFilesError({
                        kind: "error",
                        title: t(
                          "modal.uploadBill.errorMessages.filesMissing.title"
                        ),
                      });
                    }
                    handlePhoneNumberInput(phoneNumber);
                    handleBillAmountInput(billAmount);
                    console.error("Please fill in all fields correctly.");
                  }
                  // setShowComplete(true);
                },
              },
              {
                children: t("modal.cancelButtonText"),
                kind: "secondary",
                size: "large",
                onClick: () => {
                  resetState();
                  onOpenedChange(false);
                },
              },
            ]}
          />
        </div>
      )}
    </Modal>
  );
};

const DummyInput = () => {
  /* Dummy input to prevent autofocus/blur events which will show error state on load */
  return (
    <input
      aria-hidden="true"
      style={{
        height: 0,
        width: 0,
        padding: 0,
        margin: 0,
        border: 0,
        position: "absolute",
      }}
    />
  );
};

const HowItWorks = () => {
  const { t } = useTranslation(["app"], { keyPrefix: "poyd.modal" });
  const steps = t("howItWorks.steps", { returnObjects: true });
  return Array.isArray(steps) ? (
    <HowItWorksWrapper>
      <Text type="body" size="large" bold>
        {t("howItWorks.heading")}
      </Text>
      <HowItWorksSteps>
        {steps?.map((item, index) => (
          <HowItWorksItem key={index}>
            <Icon
              name={item.icon}
              color={color.element.brandhighlight.onlightbrandneutral}
              size={32}
            />
            <Text type="body" size="medium">
              {item.text}
            </Text>
          </HowItWorksItem>
        ))}
      </HowItWorksSteps>
    </HowItWorksWrapper>
  ) : null;
};

const TradeInRequirements = () => {
  const { t } = useTranslation(["app"], { keyPrefix: "poyd.modal" });
  const requirements = t("tradeInRequirements", { returnObjects: true });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
        <Text kind="title" size="xsmall" bold>
          {t("tradeInRequirements.header")}
        </Text>
        <Text
          kind="body"
          size="large"
          color={color.element.secondary.onlightsecondary}
        >
          {t("tradeInRequirements.subheader")}
        </Text>
      </div>
      <div
        style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}
      >
        {requirements?.content?.map((item, index) => (
          <Text key={index} kind="body" size="large">
            {`${index + 1}. ${item}`}
          </Text>
        ))}
      </div>
    </div>
  );
};

const CompletedScreen = ({
  payoffAmount,
  onContinue = () => {},
  onReupload = () => {},
  onCancel = () => {},
}) => {
  const { t } = useTranslation(["app"], { keyPrefix: "poyd" });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "3rem" }}>
      <Text
        kind="feature"
        size="small"
        color={color.element.brandhighlight.onlightbrandneutral}
        bold
      >
        {formatPriceString(payoffAmount, true)}
      </Text>
      <Text kind="body" size="large">
        {t("modal.complete.message")}
      </Text>
      <ButtonGroup
        alignment="left"
        buttonWidth="100%"
        rowQuantity={{
          mobile: 1,
          mobileSmall: 2,
          mobileLarge: 2,
          tablet: 2,
          tabletSmall: 2,
          tabletLarge: 2,
          desktop: 2,
          desktopSmall: 2,
          desktopLarge: 2,
          desktopXLarge: 2,
        }}
        items={[
          {
            children: t("modal.complete.continueButtonText"),
            kind: "primary",
            size: "large",
            onClick: onContinue,
          },
          {
            children: t("modal.complete.reuploadButtonText"),
            kind: "secondary",
            size: "large",
            onClick: onReupload,
          },
          {
            children: t("modal.complete.cancelLinkText"),
            kind: "textLink",
            size: "large",
            onClick: onCancel,
          },
        ]}
      />
    </div>
  );
};

const HowItWorksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 16px;
  background: ${color.stone};
  border-radius: 12px;
`;

const HowItWorksSteps = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  max-width: 100%;
  overflow-x: scroll;
`;

const HowItWorksItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  flex-grow: 1;
  flex-basis: 0;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props?.gap ?? "1.5rem"};
  @media screen and (max-width: 545px) {
    flex-direction: column;
  }
`;

const FormElement = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-grow: 1;
  flex-basis: 0;
`;

const FileUploadSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  @media screen and (max-width: 545px) {
    flex-direction: column;
  }
`;

const FileUploadTitleLockup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const FileUploadWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 0;
  min-height: 140px;
`;

export default POYDModal;
